import { useState } from 'react'
import Styles from './V4WebsiteEntry.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { useCreateBrandProfile } from 'hooks/queries/brandProfiles/useCreateBrandProfile'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useIsUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { useAuth } from 'hooks/UseAuth'
import { DEFAULT_LANGUAGE, LanguageInput } from 'components/Wizards/Wizard/Form/LanguageInputField/LanguageInput'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { BRAND_KIT_CREATED, trackEvent } from 'utils/trackingUtils'
import { useMemberProfile } from 'hooks/useMemberProfile'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useUpdateIterableProfile } from 'hooks/queries/useUpdateIterableProfile'
import { TopBar } from '../TopBar/TopBar'
import { BrandProfile } from 'types'

dayjs.extend(timezone)

export interface V4WebsiteEntryProps {
  onNext: (brandProfile: BrandProfile, brandVisualEnhancementId?: string) => void
  brandKitId?: number | null
  language?: string
  onLanguageChange: (language: string) => void
}

const V4WebsiteEntry = ({ onNext, brandKitId, language, onLanguageChange }: V4WebsiteEntryProps) => {
  const [newUrl, setNewUrl] = useState('')
  const [selectedLanguage, setSelectedLanguage] = useState(language || DEFAULT_LANGUAGE)

  const { isValid } = useIsUrlCrawlable(newUrl.trim())
  const { mutateAsync: updateIterableProfile } = useUpdateIterableProfile()
  const { mutate, isLoading: isLoadingProfile } = useCreateBrandProfile()
  const { mutateAsync: createSource, isLoading: isLoadingSource } = useCreateSource()
  const { mutateAsync: createBrandKitSource, isLoading: isLoadingBrandKitSource } = useCreateBrandKitSource()
  const { trackWebsiteUrlSubmitted } = useOnboardingTracking()
  const auth = useAuth()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { profile } = useMemberProfile(currentWorkspaceId, auth?.id)
  const { isMobile } = useResponsiveDesign()

  const isLoading = isLoadingSource || isLoadingBrandKitSource || isLoadingProfile
  const canSubmit = !isLoading && isValid && newUrl.trim() !== ''

  const handleSubmit = async () => {
    const url = newUrl.trim()
    if (!url || isLoading) return

    trackWebsiteUrlSubmitted(url)

    if (brandKitId) {
      const source = await createSource({ url, type: 'Website' })
      await createBrandKitSource({
        brandKitId,
        source: { type: 'Website', id: source.source.id },
      })
    }

    mutate(
      {
        url,
        document_media_object_ids: [],
        brandKitId,
        language: selectedLanguage,
        timezone: dayjs.tz.guess(),
      },
      {
        onSuccess: (data) => {
          trackEvent(BRAND_KIT_CREATED, profile)
          updateIterableProfile({ onboarding_device: isMobile ? 'mobile' : 'desktop' })
          onNext(data.brand_profile, undefined)
        },
        onError: (error) => {
          showErrorMessageToast(error.message)
        },
      },
    )
  }

  return (
    <div className={Styles.page}>
      <TopBar highlightStep="gettingStarted" />
      <div className={Styles.body}>
        <div className={Styles.content}>
          <div className={Styles.textBlock}>
            <p className={Styles.heading}>Let's build your content engine</p>
            <p className={Styles.subtitle}>Enter your website. We handle the rest.</p>
          </div>

          <div className={Styles.formArea}>
            <div className={Styles.inputRow}>
              <div className={Styles.inputWrap}>
                <Input
                  size="xl"
                  name="website"
                  type="url"
                  placeholder="yourbusiness.com"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  autoFocus={!isMobile}
                  autoComplete="off"
                  autoCapitalize="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSubmit) handleSubmit()
                  }}
                />
              </div>
              <Button
                id="brand-profile-website-next"
                aria-label="Analyze my business"
                size="xl"
                className={Styles.analyzeButton}
                onPress={handleSubmit}
                isDisabled={!canSubmit}
              >
                Analyze my business
              </Button>
            </div>

            <div className={Styles.languageRow}>
              <Text variant="secondary" color="var(--dark-60)">
                Content language
              </Text>
              <LanguageInput
                onLanguageChange={(lang) => {
                  setSelectedLanguage(lang)
                  onLanguageChange(lang)
                }}
                languageValue={selectedLanguage}
                size="md"
                wrapperClassName={Styles.languageInputWrapper}
                contentsClassName={Styles.languageInputContents}
                popoverClassName={Styles.languageInputPopover}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { V4WebsiteEntry }
